import React, { useEffect, useMemo, useState } from "react";
import { Search, User, FolderKanban, Plus, X, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function AdminAccess({ refs }) {
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState("user"); // user | project
  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveUserPrograms = async (u, programs) => {
    await api.put(`/users/${u.id}`, { programs });
    setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, programs } : x)));
    if (selectedUser?.id === u.id) setSelectedUser({ ...u, programs });
    toast.success("Access updated");
  };

  const toggleUserProgram = (u, pid) => {
    const programs = u.programs?.includes(pid) ? u.programs.filter((x) => x !== pid) : [...(u.programs || []), pid];
    saveUserPrograms(u, programs);
  };

  const filteredUsers = useMemo(() => users.filter((u) =>
    u.name.toLowerCase().includes(query.toLowerCase()) || u.email.toLowerCase().includes(query.toLowerCase())
  ), [users, query]);

  const inProgram = (pid) => users.filter((u) => u.programs?.includes(pid));
  const notInProgram = (pid) => users.filter((u) => !u.programs?.includes(pid));

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => { setMode("user"); setQuery(""); }} data-testid="access-mode-user"
          className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2 border ${mode === "user" ? "border-primary bg-primary/5 text-primary font-medium" : "border-slate-200 text-slate-500"}`}>
          <User className="w-4 h-4" /> By User
        </button>
        <button onClick={() => { setMode("project"); setQuery(""); }} data-testid="access-mode-project"
          className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2 border ${mode === "project" ? "border-primary bg-primary/5 text-primary font-medium" : "border-slate-200 text-slate-500"}`}>
          <FolderKanban className="w-4 h-4" /> By Project
        </button>
      </div>

      {mode === "user" ? (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people…" className="pl-9" data-testid="access-user-search" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 max-h-[60vh] overflow-y-auto kp-scroll">
              {filteredUsers.map((u) => (
                <button key={u.id} onClick={() => setSelectedUser(u)} data-testid={`access-user-${u.id}`}
                  className={`w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between ${selectedUser?.id === u.id ? "bg-primary/5" : ""}`}>
                  <div><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                  <Badge variant="outline">{u.programs?.length || 0}</Badge>
                </button>
              ))}
            </div>
          </div>
          <div>
            {selectedUser ? (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-sm font-semibold text-slate-800">{selectedUser.name}</p>
                <p className="text-xs text-slate-400 mb-4">{refs.rcName(selectedUser.role_category)} · {refs.rlName(selectedUser.role_level)?.split(" ")[0]}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Assigned programs</p>
                <div className="space-y-2">
                  {refs.programs.map((p) => {
                    const on = selectedUser.programs?.includes(p.id);
                    return (
                      <button key={p.id} onClick={() => toggleUserProgram(selectedUser, p.id)} data-testid={`assign-${selectedUser.id}-${p.id}`}
                        className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${on ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-500"}`}>
                        <span>{p.name}</span>
                        {on ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : <p className="text-sm text-slate-400 p-6 text-center border border-dashed border-slate-300 rounded-xl">Select a person to manage their programs.</p>}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Programs</p>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {refs.programs.map((p) => (
                <button key={p.id} onClick={() => setSelectedProgram(p)} data-testid={`access-program-${p.id}`}
                  className={`w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between ${selectedProgram?.id === p.id ? "bg-primary/5" : ""}`}>
                  <div><p className="text-sm font-medium text-slate-800">{p.name}</p><p className="text-xs font-mono text-slate-400">{p.jira_tag}</p></div>
                  <Badge variant="outline">{inProgram(p.id).length}</Badge>
                </button>
              ))}
            </div>
          </div>
          <div>
            {selectedProgram ? (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <p className="text-sm font-semibold text-slate-800 mb-3">{selectedProgram.name}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Assigned ({inProgram(selectedProgram.id).length})</p>
                <div className="space-y-1.5 mb-4">
                  {inProgram(selectedProgram.id).map((u) => (
                    <div key={u.id} className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/15 px-3 py-1.5">
                      <span className="text-sm text-slate-700">{u.name}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-500"
                        onClick={() => toggleUserProgram(u, selectedProgram.id)} data-testid={`remove-${u.id}-${selectedProgram.id}`}><X className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Add people</p>
                <div className="space-y-1.5 max-h-52 overflow-y-auto kp-scroll">
                  {notInProgram(selectedProgram.id).map((u) => (
                    <div key={u.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5">
                      <span className="text-sm text-slate-600">{u.name}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-primary"
                        onClick={() => toggleUserProgram(u, selectedProgram.id)} data-testid={`add-${u.id}-${selectedProgram.id}`}><Plus className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-slate-400 p-6 text-center border border-dashed border-slate-300 rounded-xl">Select a program to manage its members.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
